import { useState, useEffect, useRef, useCallback } from 'react';
import { chatAPI, noteAPI, chainAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, Send, Plus, Users, User, SmilePlus, Share2, FileText, BookOpen, X, Check, CheckCheck } from 'lucide-react';

const REACTIONS = ['👍', '❤️', '😂', '🔥', '💯', '🤔'];

function MessageBubble({ msg, isMe, onReact }) {
  const [showReactions, setShowReactions] = useState(false);

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-3 animate-fade-in`} data-testid={`msg-${msg.id}`}>
      <div className={`max-w-[70%] relative group`}>
        {!isMe && (
          <span className="font-hud text-[9px] text-[#2F8A92] tracking-wider mb-0.5 block">{msg.sender_name}</span>
        )}
        <div
          className={`px-3 py-2 rounded-lg relative ${
            isMe
              ? 'bg-[#2F8A92]/15 border border-[#2F8A92]/20 text-white'
              : 'bg-[#1a1a2e] border border-[#ffffff]/5 text-[#C0C0C0]'
          }`}
        >
          <p className="font-mono text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>

          {/* Shared content card */}
          {msg.shared_content && (
            <div className="mt-2 p-2 rounded border border-[#D4A84A]/20 bg-[#D4A84A]/5">
              <div className="flex items-center gap-1.5 mb-1">
                {msg.shared_content.type === 'note' ? <FileText size={10} className="text-[#D4A84A]" /> : <BookOpen size={10} className="text-[#D4A84A]" />}
                <span className="font-hud text-[8px] text-[#D4A84A] tracking-wider uppercase">{msg.shared_content.type}</span>
              </div>
              <p className="font-mono text-xs text-white/80">{msg.shared_content.title}</p>
            </div>
          )}

          <div className="flex items-center justify-between mt-1">
            <span className="font-mono text-[8px] text-[#52525B]">
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            {isMe && (
              <span className="ml-2">
                {msg.read_by?.length > 1
                  ? <CheckCheck size={10} className="text-[#2F8A92]" />
                  : <Check size={10} className="text-[#52525B]" />
                }
              </span>
            )}
          </div>
        </div>

        {/* Reactions display */}
        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(msg.reactions).map(([emoji, users]) => (
              <button
                key={emoji}
                onClick={() => onReact(msg.id, emoji)}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#1a1a2e] border border-[#ffffff]/5 text-xs hover:border-[#2F8A92]/30 transition-all duration-300"
              >
                <span>{emoji}</span>
                <span className="font-mono text-[9px] text-white/80">{users.length}</span>
              </button>
            ))}
          </div>
        )}

        {/* React button */}
        <button
          onClick={() => setShowReactions(!showReactions)}
          className="absolute -top-2 right-2 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-[#0A0A0A] rounded-full p-1 border border-[#2F8A92]/10"
        >
          <SmilePlus size={10} className="text-white/80" />
        </button>

        {/* Reaction picker */}
        {showReactions && (
          <div className="absolute -top-8 right-0 flex items-center gap-1 px-2 py-1 rounded-lg border border-[#2F8A92]/20 z-10" style={{ background: '#0A0A0A' }}>
            {REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => { onReact(msg.id, emoji); setShowReactions(false); }}
                className="text-sm hover:scale-125 transition-transform duration-200"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RoomList({ rooms, activeRoom, onSelect, onNewRoom, unreadCounts }) {
  return (
    <div className="h-full flex flex-col border-r border-[#2F8A92]/8" style={{ width: '280px' }} data-testid="chat-room-list">
      <div className="flex items-center justify-between p-3 border-b border-[#2F8A92]/8">
        <h3 className="font-hud text-[11px] text-[#2F8A92] tracking-widest uppercase">Messages</h3>
        <button
          data-testid="new-chat-btn"
          onClick={onNewRoom}
          className="text-[#2F8A92] hover:text-[#66FAFF] transition-all duration-300"
        >
          <Plus size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rooms.map(room => {
          const isActive = activeRoom?.id === room.id;
          const unread = unreadCounts[room.id] || 0;
          return (
            <button
              key={room.id}
              data-testid={`room-${room.id}`}
              onClick={() => onSelect(room)}
              className={`w-full p-3 text-left transition-all duration-300 border-b border-[#ffffff]/3 ${
                isActive ? 'bg-[#2F8A92]/5' : 'hover:bg-[#ffffff]/2'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  room.is_group ? 'bg-[#D4A84A]/10 border border-[#D4A84A]/20' : 'bg-[#2F8A92]/10 border border-[#2F8A92]/20'
                }`}>
                  {room.is_group ? <Users size={12} className="text-[#D4A84A]" /> : <User size={12} className="text-[#2F8A92]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-hud text-[10px] text-white tracking-wider truncate">{room.name}</span>
                    {unread > 0 && (
                      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-[#2F8A92] text-[#0A0A0A] font-hud text-[8px] flex items-center justify-center">{unread}</span>
                    )}
                  </div>
                  {room.last_message && (
                    <p className="font-mono text-[9px] text-[#52525B] truncate mt-0.5">{room.last_message}</p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
        {rooms.length === 0 && (
          <div className="p-4 text-center">
            <p className="font-mono text-xs text-[#52525B]">No conversations yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ShareContentModal({ onShare, onClose }) {
  const [notes, setNotes] = useState([]);
  const [chains, setChains] = useState([]);
  const [tab, setTab] = useState('notes');

  useEffect(() => {
    noteAPI.list().then(r => setNotes(r.data)).catch(() => {});
    chainAPI.list().then(r => setChains(r.data)).catch(() => {});
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="p-5 w-full max-w-md animate-fade-in border border-[#2F8A92]/15 rounded-lg" style={{ background: '#0A0A0A' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-hud text-sm text-[#2F8A92] tracking-wider">Share Content</h3>
          <button onClick={onClose} className="text-[#52525B] hover:text-[#2F8A92]"><X size={16} /></button>
        </div>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setTab('notes')} className={`px-3 py-1 font-hud text-[10px] tracking-wider ${tab === 'notes' ? 'text-[#2F8A92] border-b border-[#2F8A92]' : 'text-[#52525B]'}`}>NOTES</button>
          <button onClick={() => setTab('chains')} className={`px-3 py-1 font-hud text-[10px] tracking-wider ${tab === 'chains' ? 'text-[#D4A84A] border-b border-[#D4A84A]' : 'text-[#52525B]'}`}>CHAINS</button>
        </div>
        <div className="max-h-[300px] overflow-y-auto space-y-2">
          {tab === 'notes' && notes.map(note => (
            <button
              key={note.id}
              onClick={() => onShare({ type: 'note', id: note.id, title: note.title })}
              className="w-full p-3 text-left border border-[#2F8A92]/10 hover:border-[#2F8A92]/30 rounded transition-all duration-300"
            >
              <FileText size={12} className="text-[#2F8A92] inline mr-2" />
              <span className="font-mono text-xs text-white/80">{note.title}</span>
            </button>
          ))}
          {tab === 'chains' && chains.map(chain => (
            <button
              key={chain.id}
              onClick={() => onShare({ type: 'chain', id: chain.id, title: chain.subject })}
              className="w-full p-3 text-left border border-[#D4A84A]/10 hover:border-[#D4A84A]/30 rounded transition-all duration-300"
            >
              <BookOpen size={12} className="text-[#D4A84A] inline mr-2" />
              <span className="font-mono text-xs text-white/80">{chain.subject}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TeacherChat() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showNewRoom, setShowNewRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomMembers, setNewRoomMembers] = useState('');
  const [showShare, setShowShare] = useState(false);
  const scrollRef = useRef(null);
  const typingTimeout = useRef(null);
  const pollRef = useRef(null);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await chatAPI.rooms();
      setRooms(res.data);
    } catch (e) { console.warn('[TeacherChat] fetchRooms failed', e); }
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!activeRoom) return;
    try {
      const res = await chatAPI.messages(activeRoom.id);
      setMessages(res.data);
    } catch (e) { console.warn('[TeacherChat] fetchMessages failed', e); }
  }, [activeRoom]);

  const fetchTyping = useCallback(async () => {
    if (!activeRoom) return;
    try {
      const res = await chatAPI.getTyping(activeRoom.id);
      setTypingUsers(res.data);
    } catch (e) { console.warn('[TeacherChat] fetchTyping failed', e); }
  }, [activeRoom]);

  // Initial load
  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  // Poll messages + typing every 3 seconds
  useEffect(() => {
    if (!activeRoom) return;
    fetchMessages();
    const interval = setInterval(() => {
      fetchMessages();
      fetchTyping();
    }, 3000);
    pollRef.current = interval;
    return () => clearInterval(interval);
  }, [activeRoom, fetchMessages, fetchTyping]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (sharedContent = null) => {
    if (!input.trim() && !sharedContent) return;
    if (!activeRoom) return;
    try {
      await chatAPI.send(activeRoom.id, input.trim() || (sharedContent ? `Shared: ${sharedContent.title}` : ''), sharedContent);
      setInput('');
      fetchMessages();
      fetchRooms();
    } catch (e) { console.warn('[TeacherChat] handleSend failed', e); }
  };

  const handleTyping = () => {
    if (!activeRoom) return;
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    chatAPI.typing(activeRoom.id).catch(() => {});
    typingTimeout.current = setTimeout(() => {}, 4000);
  };

  const handleReact = async (messageId, emoji) => {
    try {
      await chatAPI.react(messageId, emoji);
      fetchMessages();
    } catch (e) { console.warn('[TeacherChat] handleReact failed', e); }
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;
    const members = newRoomMembers.split(',').map(e => e.trim()).filter(Boolean);
    try {
      const res = await chatAPI.createRoom(newRoomName.trim(), members, members.length > 0);
      setShowNewRoom(false);
      setNewRoomName('');
      setNewRoomMembers('');
      fetchRooms();
      setActiveRoom(res.data);
    } catch (e) { console.warn('[TeacherChat] handleCreateRoom failed', e); }
  };

  const handleShare = (content) => {
    setShowShare(false);
    handleSend(content);
  };

  return (
    <div className="h-full flex" style={{ minHeight: '400px' }} data-testid="teacher-chat">
      {/* Room List */}
      <RoomList
        rooms={rooms}
        activeRoom={activeRoom}
        onSelect={setActiveRoom}
        onNewRoom={() => setShowNewRoom(true)}
        unreadCounts={unreadCounts}
      />

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeRoom ? (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-[#2F8A92]/8">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  activeRoom.is_group ? 'bg-[#D4A84A]/10' : 'bg-[#2F8A92]/10'
                }`}>
                  {activeRoom.is_group ? <Users size={10} className="text-[#D4A84A]" /> : <User size={10} className="text-[#2F8A92]" />}
                </div>
                <span className="font-hud text-[11px] text-white tracking-wider">{activeRoom.name}</span>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4" data-testid="chat-messages">
              {messages.map(msg => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  isMe={msg.sender_id === user?._id}
                  onReact={handleReact}
                />
              ))}
              {typingUsers.length > 0 && (
                <div className="flex items-center gap-2 mb-2 animate-fade-in">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2F8A92] animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2F8A92] animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2F8A92] animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </div>
                  <span className="font-mono text-[9px] text-[#52525B]">{typingUsers.join(', ')} typing...</span>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-[#2F8A92]/8 flex items-center gap-2" data-testid="chat-input-area">
              <button
                data-testid="chat-share-btn"
                onClick={() => setShowShare(true)}
                className="text-white/80 hover:text-[#D4A84A] transition-all duration-300"
                title="Share content"
              >
                <Share2 size={16} />
              </button>
              <input
                data-testid="chat-input"
                className="hud-input flex-1 text-sm"
                value={input}
                onChange={e => { setInput(e.target.value); handleTyping(); }}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
              />
              <button
                data-testid="chat-send-btn"
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="text-[#2F8A92] hover:text-[#66FAFF] disabled:text-[#52525B] transition-all duration-300"
              >
                <Send size={16} />
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-5">
            <div className="text-center max-w-lg" data-testid="chat-empty-state">
              <MessageCircle className="mx-auto text-[#2F8A92]/20 mb-3" size={40} />
              <p className="font-hud text-xs text-[#52525B] tracking-wider">Select a conversation</p>
              <p className="font-mono text-[9px] text-[#52525B] mt-1 mb-6">or start a new one</p>

              {/* Pinned: TSOGN Lessons Contest invitation — visible to teachers and applicants */}
              <div
                className="text-left border border-[#D4A84A]/25 rounded p-4 bg-[#D4A84A]/05"
                data-testid="chat-lessons-contest-pinned"
                style={{ background: 'rgba(212,168,74,0.03)' }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="font-hud text-[9px] tracking-widest text-[#D4A84A] uppercase">📌 Pinned · From Midnite</span>
                </div>
                <p className="font-historical text-lg md:text-xl text-[#D4A84A] font-light tracking-tight mb-1.5">
                  Tell us what you teach.
                </p>
                <p className="font-mono text-xs md:text-sm text-white/85 leading-relaxed mb-3">
                  We want to help make your job easier. We know you're constrained in what you can teach. Submit your lessons so we can build the tools to help you. We start with the spine where it currently is — submissions that match the chronological window get walked first.
                </p>
                <button
                  data-testid="chat-open-contest-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('tsogn:open-lessons-contest'))}
                  className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85"
                >
                  OPEN LESSONS CONTEST
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Room Modal */}
      {showNewRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="p-6 w-full max-w-sm animate-fade-in border border-[#2F8A92]/15 rounded-lg" style={{ background: '#0A0A0A' }} data-testid="new-room-modal">
            <h3 className="font-hud text-sm text-[#2F8A92] tracking-wider mb-4">New Conversation</h3>
            <div className="space-y-3">
              <div>
                <label className="font-hud text-[9px] text-white/80 tracking-wider uppercase block mb-1">Name</label>
                <input
                  data-testid="new-room-name"
                  className="hud-input text-sm"
                  value={newRoomName}
                  onChange={e => setNewRoomName(e.target.value)}
                  placeholder="Chat name..."
                />
              </div>
              <div>
                <label className="font-hud text-[9px] text-white/80 tracking-wider uppercase block mb-1">Members (emails, comma separated)</label>
                <input
                  data-testid="new-room-members"
                  className="hud-input text-sm"
                  value={newRoomMembers}
                  onChange={e => setNewRoomMembers(e.target.value)}
                  placeholder="teacher@school.edu, aide@school.edu"
                />
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="create-room-btn"
                  onClick={handleCreateRoom}
                  className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-4 py-2 hover:bg-[#66FAFF] transition-all duration-300"
                >
                  CREATE
                </button>
                <button
                  onClick={() => setShowNewRoom(false)}
                  className="font-hud text-xs text-white/80 tracking-wider hover:text-[#2F8A92] px-4 py-2 transition-all duration-300"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Content Modal */}
      {showShare && <ShareContentModal onShare={handleShare} onClose={() => setShowShare(false)} />}
    </div>
  );
}
